import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Slider,
  Stack,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DragIndicatorRoundedIcon from '@mui/icons-material/DragIndicatorRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import ForumRoundedIcon from '@mui/icons-material/ForumRounded';
import { Assignment } from '../../types/interfaces';
import {
  chatWithAssignmentTutor,
  TutorConversationMessage,
  TutorQuickAction,
} from '../../services/tutorService';
import { playSpeech, synthesizeSpeech } from '../../services/speechService';
import {
  getAssignmentReminder,
  getAssignmentTopic,
  getDefaultTutorGreeting,
  getVisibleMaterialFlag,
} from './assignmentTutorUtils';

const AUTO_SPEAK_STORAGE_KEY = 'tsemppi-auto-speak-enabled';
const TUTOR_PANEL_OPEN_STORAGE_KEY = 'tsemppi-panel-open';
const TUTOR_PANEL_WIDTH_STORAGE_KEY = 'tsemppi-panel-width';
const TUTOR_VOICE_CODE = 'elevenlabs:6fqvQ9BPO8j0m0UmCkfI';
const DEFAULT_PANEL_WIDTH = 420;
const MIN_PANEL_WIDTH = 320;
const MAX_PANEL_WIDTH = 640;
const COLLAPSED_PANEL_WIDTH = 74;
const RESIZE_HANDLE_WIDTH = 22;

interface StudentTutorDrawerProps {
  assignmentId: number;
  assignment: Assignment | null;
  currentAnswers: Array<string | string[]>;
  studentName?: string;
}

interface ChatMessage {
  id: string;
  role: 'assistant' | 'user';
  content: string;
}

const createMessage = (role: 'assistant' | 'user', content: string): ChatMessage => ({
  id: `${role}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  role,
  content,
});

const clampPanelWidth = (width: number) => Math.min(MAX_PANEL_WIDTH, Math.max(MIN_PANEL_WIDTH, width));

const StudentTutorDrawer: React.FC<StudentTutorDrawerProps> = ({
  assignmentId,
  assignment,
  currentAnswers,
  studentName,
}) => {
  const maxMessageLength = 1000;
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [open, setOpen] = useState<boolean>(() => {
    if (typeof window === 'undefined') {
      return false;
    }

    return window.localStorage.getItem(TUTOR_PANEL_OPEN_STORAGE_KEY) === 'true';
  });
  const [panelWidth, setPanelWidth] = useState<number>(() => {
    if (typeof window === 'undefined') {
      return DEFAULT_PANEL_WIDTH;
    }

    const storedWidth = Number(window.localStorage.getItem(TUTOR_PANEL_WIDTH_STORAGE_KEY));
    return Number.isFinite(storedWidth) ? clampPanelWidth(storedWidth) : DEFAULT_PANEL_WIDTH;
  });
  const [isResizing, setIsResizing] = useState(false);
  const [input, setInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const resizeStartXRef = useRef(0);
  const resizeStartWidthRef = useRef(DEFAULT_PANEL_WIDTH);
  const [autoSpeakEnabled, setAutoSpeakEnabled] = useState<boolean>(() => {
    if (typeof window === 'undefined') {
      return true;
    }

    const storedValue = window.localStorage.getItem(AUTO_SPEAK_STORAGE_KEY);
    return storedValue === null ? true : storedValue === 'true';
  });
  const [quickActions, setQuickActions] = useState<TutorQuickAction[]>([
    {
      id: 'small-hint',
      label: 'Anna pieni vihje',
      prompt: 'Anna minulle pieni vihje ilman spoileria.',
    },
    {
      id: 'simpler',
      label: 'Selitä helpommin',
      prompt: 'Selitä tämä helpommin ja rennommin, mutta älä anna vastausta.',
    },
    {
      id: 'start',
      label: getVisibleMaterialFlag(assignment) ? 'Mistä materiaalista katson?' : 'Mistä aloitan?',
      prompt: getVisibleMaterialFlag(assignment)
        ? 'Kerro mitä kohtaa materiaalista minun kannattaa katsoa ensiksi.'
        : 'Kerro mistä minun kannattaa aloittaa tässä tehtävässä.',
    },
  ]);
  const [messages, setMessages] = useState<ChatMessage[]>([
    createMessage('assistant', getDefaultTutorGreeting(studentName, assignment)),
  ]);

  const topic = useMemo(() => getAssignmentTopic(assignment), [assignment]);
  const reminder = useMemo(() => getAssignmentReminder(assignment), [assignment]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(AUTO_SPEAK_STORAGE_KEY, String(autoSpeakEnabled));
    }
  }, [autoSpeakEnabled]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(TUTOR_PANEL_OPEN_STORAGE_KEY, String(open));
    }
  }, [open]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(TUTOR_PANEL_WIDTH_STORAGE_KEY, String(panelWidth));
    }
  }, [panelWidth]);

  useEffect(() => {
    if (fullScreen) {
      setIsResizing(false);
    }
  }, [fullScreen]);

  useEffect(() => {
    if (!isResizing) {
      return undefined;
    }

    const handleMouseMove = (event: MouseEvent) => {
      const deltaX = resizeStartXRef.current - event.clientX;
      setPanelWidth(clampPanelWidth(resizeStartWidthRef.current + deltaX));
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  const playTutorReply = async (message: ChatMessage) => {
    if (!autoSpeakEnabled || message.role !== 'assistant' || !message.content.trim()) {
      return;
    }

    setIsSpeaking(true);

    try {
      const audioArrayBuffer = await synthesizeSpeech(message.content, TUTOR_VOICE_CODE);
      await playSpeech(audioArrayBuffer, () => undefined);
    } catch (error) {
      console.error('Tutor speech playback failed:', error);
    } finally {
      setIsSpeaking(false);
    }
  };

  const sendMessage = async (message: string) => {
    const trimmedMessage = message.trim();

    if (!trimmedMessage || isSending) {
      return;
    }

    const nextUserMessage = createMessage('user', trimmedMessage);
    const nextConversation = [...messages, nextUserMessage];
    setMessages(nextConversation);
    setInput('');
    setIsSending(true);

    try {
      const response = await chatWithAssignmentTutor(assignmentId, {
        message: trimmedMessage,
        conversationHistory: nextConversation.map<TutorConversationMessage>((chatMessage) => ({
          role: chatMessage.role,
          content: chatMessage.content,
        })),
        currentAnswers,
        includeMaterialContext: getVisibleMaterialFlag(assignment),
      });

      const assistantMessage = createMessage('assistant', response.data.reply);

      setMessages((previousMessages) => [
        ...previousMessages,
        assistantMessage,
      ]);

      void playTutorReply(assistantMessage);

      if (Array.isArray(response.data.quickActions) && response.data.quickActions.length > 0) {
        setQuickActions(response.data.quickActions.slice(0, 3));
      }
    } catch (error) {
      console.error('Tutor chat failed:', error);
      const fallbackMessage = createMessage(
        'assistant',
        'Oho, nyt linjoissa oli pieni kompastus. En anna periksi ihan noin vain, joten kokeile sama uudestaan tai paina joku pikavaihtoehdoista.',
      );

      setMessages((previousMessages) => [
        ...previousMessages,
        fallbackMessage,
      ]);

      void playTutorReply(fallbackMessage);
    } finally {
      setIsSending(false);
    }
  };

  const handleSubmit = () => {
    void sendMessage(input);
  };

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();

      if (!isSending && input.trim()) {
        handleSubmit();
      }
    }
  };

  const handleResizeStart = (event: React.MouseEvent<HTMLDivElement>) => {
    resizeStartXRef.current = event.clientX;
    resizeStartWidthRef.current = panelWidth;
    setIsResizing(true);
  };

  const handleWidthChange = (_event: Event, value: number | number[]) => {
    if (typeof value === 'number') {
      setPanelWidth(clampPanelWidth(value));
    }
  };

  const showDesktopPanel = !fullScreen;
  const containerWidth = showDesktopPanel ? (open ? panelWidth : COLLAPSED_PANEL_WIDTH) : '100%';

  return (
    <Box
      sx={{
        width: containerWidth,
        flexShrink: 0,
        alignSelf: 'stretch',
        transition: showDesktopPanel && !isResizing ? 'width 180ms ease' : 'none',
      }}
    >
      <Paper
        elevation={0}
        sx={{
          position: showDesktopPanel ? 'sticky' : 'relative',
          top: showDesktopPanel ? 24 : 0,
          height: showDesktopPanel ? 'calc(100vh - 48px)' : 'auto',
          minHeight: showDesktopPanel ? 560 : 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          borderRadius: 4,
          border: '1px solid',
          borderColor: open ? 'rgba(14, 116, 144, 0.18)' : 'rgba(148, 163, 184, 0.24)',
          backgroundColor: '#ffffff',
          boxShadow: open ? '0 24px 60px rgba(15, 23, 42, 0.12)' : '0 12px 30px rgba(15, 23, 42, 0.08)',
        }}
      >
        {showDesktopPanel && open && (
          <Box
            onMouseDown={handleResizeStart}
            onDoubleClick={() => setPanelWidth(DEFAULT_PANEL_WIDTH)}
            sx={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: 0,
              width: RESIZE_HANDLE_WIDTH,
              cursor: 'col-resize',
              zIndex: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'linear-gradient(180deg, rgba(14,165,233,0.08) 0%, rgba(30,64,175,0.12) 100%)',
              borderRight: '1px solid rgba(148, 163, 184, 0.25)',
              transition: 'background 150ms ease',
              '&:hover': {
                background: 'linear-gradient(180deg, rgba(14,165,233,0.14) 0%, rgba(30,64,175,0.18) 100%)',
              },
            }}
          >
            <Box
              sx={{
                width: 6,
                height: 72,
                borderRadius: '999px',
                background: 'repeating-linear-gradient(180deg, rgba(15,23,42,0.42) 0 4px, transparent 4px 8px)',
              }}
            />
          </Box>
        )}

        {!open ? (
          <Box
            sx={{
              height: '100%',
              minHeight: showDesktopPanel ? 560 : 'auto',
              display: 'flex',
              flexDirection: showDesktopPanel ? 'column' : 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5,
              p: 1.5,
              background: 'linear-gradient(180deg, #ecfeff 0%, #eff6ff 100%)',
            }}
          >
            <IconButton
              onClick={() => setOpen(true)}
              sx={{
                width: 48,
                height: 48,
                color: 'white',
                background: 'linear-gradient(135deg, #0f766e 0%, #1d4ed8 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #115e59 0%, #1e40af 100%)',
                },
              }}
            >
              <AutoAwesomeRoundedIcon />
            </IconButton>
            <Typography
              variant="body2"
              fontWeight={700}
              sx={{
                color: '#0f172a',
                writingMode: showDesktopPanel ? 'vertical-rl' : 'horizontal-tb',
                transform: showDesktopPanel ? 'rotate(180deg)' : 'none',
                textAlign: 'center',
                letterSpacing: 0.2,
              }}
            >
              Avaa Tsemppi
            </Typography>
          </Box>
        ) : (
          <Box
            display="flex"
            flexDirection="column"
            height="100%"
            sx={{
              ml: showDesktopPanel ? `${RESIZE_HANDLE_WIDTH}px` : 0,
            }}
          >
          <Box
            sx={{
              p: 2.5,
              color: 'white',
              background: 'linear-gradient(160deg, #0f766e 0%, #1d4ed8 55%, #172554 100%)',
            }}
          >
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Box display="flex" alignItems="center" gap={1.5}>
                <ForumRoundedIcon />
                <Typography variant="h5" fontWeight={700}>
                  Tsemppi AI Tutor
                </Typography>
              </Box>
              <Stack direction="row" spacing={0.5} alignItems="center">
                {showDesktopPanel && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      color: 'rgba(255,255,255,0.75)',
                      pr: 0.5,
                    }}
                  >
                    <DragIndicatorRoundedIcon fontSize="small" />
                    <Typography variant="caption">Vedä leveämmäksi</Typography>
                  </Box>
                )}
                <IconButton onClick={() => setOpen(false)} sx={{ color: 'white' }}>
                  <CloseRoundedIcon />
                </IconButton>
              </Stack>
            </Box>
            <Typography variant="body2" sx={{ opacity: 0.95, mb: 1 }}>
              Hauska opiskelukaveri, joka auttaa ajattelemaan mutta ei tipauta valmista vastausta suoraan syliin.
            </Typography>
            <FormControlLabel
              control={(
                <Switch
                  checked={autoSpeakEnabled}
                  onChange={(event) => setAutoSpeakEnabled(event.target.checked)}
                  color="default"
                />
              )}
              label={autoSpeakEnabled ? 'Tsempin ääni on päällä' : 'Tsempin ääni on pois päältä'}
              sx={{
                m: 0,
                color: 'white',
                '& .MuiFormControlLabel-label': {
                  fontSize: '0.875rem',
                },
              }}
            />
            {showDesktopPanel && (
              <Box
                sx={{
                  mt: 1.5,
                  mb: 1,
                  px: 1.25,
                  py: 1,
                  borderRadius: 3,
                  backgroundColor: 'rgba(255,255,255,0.12)',
                  border: '1px solid rgba(255,255,255,0.14)',
                }}
              >
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                  <Typography variant="caption" sx={{ opacity: 0.88 }}>
                    Säädä leveys itse
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.88 }}>
                    {panelWidth}px
                  </Typography>
                </Box>
                <Slider
                  value={panelWidth}
                  min={MIN_PANEL_WIDTH}
                  max={MAX_PANEL_WIDTH}
                  step={10}
                  onChange={handleWidthChange}
                  aria-label="Tsempin leveys"
                  sx={{
                    color: 'white',
                    py: 0.5,
                    '& .MuiSlider-thumb': {
                      backgroundColor: 'white',
                    },
                    '& .MuiSlider-track': {
                      border: 'none',
                    },
                    '& .MuiSlider-rail': {
                      opacity: 0.35,
                    },
                  }}
                />
                <Stack direction="row" spacing={1} mt={0.5}>
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => setPanelWidth(340)}
                    sx={{ color: 'white', minWidth: 0, px: 0 }}
                  >
                    Kapea
                  </Button>
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => setPanelWidth(DEFAULT_PANEL_WIDTH)}
                    sx={{ color: 'white', minWidth: 0, px: 0 }}
                  >
                    Normaali
                  </Button>
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => setPanelWidth(560)}
                    sx={{ color: 'white', minWidth: 0, px: 0 }}
                  >
                    Leveä
                  </Button>
                </Stack>
              </Box>
            )}
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip label={topic} size="small" sx={{ backgroundColor: 'rgba(255,255,255,0.18)', color: 'white' }} />
              <Chip label="Ei spoilereita" size="small" sx={{ backgroundColor: 'rgba(255,255,255,0.18)', color: 'white' }} />
            </Stack>
          </Box>

          <Box p={2}>
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              {reminder}
            </Alert>
          </Box>

          <Box flex={1} px={2} pb={2} sx={{ overflowY: 'auto' }}>
            {messages.map((message) => (
              <Box
                key={message.id}
                display="flex"
                justifyContent={message.role === 'assistant' ? 'flex-start' : 'flex-end'}
                mb={1.5}
              >
                <Paper
                  elevation={0}
                  sx={{
                    maxWidth: '88%',
                    px: 1.5,
                    py: 1.25,
                    borderRadius: 3,
                    backgroundColor: message.role === 'assistant' ? '#e0f2fe' : '#dbeafe',
                    border: '1px solid',
                    borderColor: message.role === 'assistant' ? '#7dd3fc' : '#93c5fd',
                  }}
                >
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {message.content}
                  </Typography>
                </Paper>
              </Box>
            ))}

            {isSending && (
              <Box display="flex" alignItems="center" gap={1} px={1}>
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">
                  Tsemppi miettii seuraavaa vihjettä...
                </Typography>
              </Box>
            )}

            {!isSending && isSpeaking && (
              <Box display="flex" alignItems="center" gap={1} px={1}>
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">
                  Tsemppi puhuu vastaustaan...
                </Typography>
              </Box>
            )}
          </Box>

          <Divider />

          <Box p={2}>
            <Typography variant="subtitle2" gutterBottom>
              Pikavaihtoehdot
            </Typography>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" mb={2}>
              {quickActions.slice(0, 3).map((action) => (
                <Button
                  key={action.id}
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    void sendMessage(action.prompt);
                  }}
                  disabled={isSending}
                  sx={{ borderRadius: '999px' }}
                >
                  {action.label}
                </Button>
              ))}
            </Stack>

            <TextField
              fullWidth
              multiline
              rows={3}
              value={input}
              onChange={(event) => setInput(event.target.value.slice(0, maxMessageLength))}
              onKeyDown={handleInputKeyDown}
              placeholder="Kysy vaikka: voitko antaa vihjeen tai selittää tämän eri tavalla?"
              helperText={`${input.length}/${maxMessageLength}`}
              inputProps={{ maxLength: maxMessageLength }}
              sx={{
                '& .MuiInputBase-inputMultiline': {
                  overflowY: 'auto',
                },
              }}
            />

            <Button
              fullWidth
              variant="contained"
              endIcon={<SendRoundedIcon />}
              onClick={handleSubmit}
              disabled={isSending || !input.trim()}
              sx={{ mt: 1.5, borderRadius: 3, py: 1.1 }}
            >
              Lähetä kysymys
            </Button>
          </Box>
        </Box>
        )}
      </Paper>
    </Box>
  );
};

export default StudentTutorDrawer;